import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { catalogApi, cartApi } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PriceDisplay } from '@/components/PriceDisplay';
import { ProductImage } from '@/components/ProductImage';
import { withClickSpan } from '@/lib/clickSpan';
import { useUiStore } from '@/stores/ui';
import { toast } from '@/components/ui/sonner';

export function Product() {
  const { productId = '' } = useParams<{ productId: string }>();
  const qc = useQueryClient();
  const openCart = useUiStore((s) => s.openCart);

  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', productId],
    queryFn: async () => (await catalogApi.get(productId)).data,
    enabled: !!productId,
  });

  const addMut = useMutation({
    mutationFn: () => cartApi.add(productId, 1),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cart'] });
      toast.success(`${product?.name ?? 'Item'} added to cart`);
      openCart();
    },
    onError: (err: Error & { traceId?: string | null }) => {
      toast.error('Failed to add to cart', { description: err.traceId ?? err.message });
    },
  });

  if (isLoading) return <Skeleton className="h-96" />;
  if (error || !product) {
    return <p className="text-(--color-ink-muted)">Could not load product.</p>;
  }

  const out = product.stock <= 0;

  return (
    <article className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="rounded-lg border bg-card overflow-hidden">
        <ProductImage
          src={product.image_url || undefined}
          fallbackSrc={`/images/${product.sku.toLowerCase()}.png`}
          alt={product.name}
          category={product.category}
          sku={product.sku}
          className="product-image"
          loading="eager"
        />
      </div>
      <div className="space-y-4">
        <Badge variant="secondary">{product.category}</Badge>
        <h1 className="font-display text-3xl">{product.name}</h1>
        <PriceDisplay knuts={product.price_knuts} className="text-2xl" />
        {out && <Badge variant="destructive">Out of stock</Badge>}
        <p className="text-(--color-ink-muted) leading-relaxed">{product.description}</p>
        <Button
          type="button"
          variant="cta"
          size="lg"
          disabled={out || addMut.isPending}
          onClick={() =>
            withClickSpan(
              'add-to-cart',
              { 'app.product.id': product.id, 'app.product.sku': product.sku, 'app.product.category': product.category },
              () => addMut.mutateAsync(),
            )
          }
          aria-label={`Add ${product.name} to cart`}
        >
          {addMut.isPending ? 'Adding…' : 'Add to Cart'}
        </Button>
      </div>
    </article>
  );
}
